import { Button } from "@shared-ui/button";
import { formatRate, formatRateDay } from "@/features/catalog/lib/gst-rates";
import type {
	GstRatePreview,
	GstRepriceResult,
} from "@/features/catalog/services/gst-rates-api.service";
import { formatPaise } from "@/lib/format";

function plural(count: number, one: string, many = `${one}s`): string {
	return `${count} ${count === 1 ? one : many}`;
}

/**
 * What a GST rate change does to customer prices, before it is saved (R10,
 * R11). Headline first, then why nothing moves when nothing does, then the
 * samples.
 */
export function GstRatePreviewPanel({
	preview,
	newRateBp,
}: {
	preview: GstRatePreview;
	/** The rate the change brings in, for the "will use" sentence; null on cancel. */
	newRateBp: number | null;
}) {
	return (
		<div className="grid gap-3">
			<div className="grid gap-1 rounded-2xl border bg-slate-50 p-4 text-sm">
				<p className="font-semibold">
					{plural(preview.productsRepriced, "product")} re-price now.
				</p>
				{preview.scheduled ? (
					<p>
						This change takes effect on {formatRateDay(preview.startsAt)}.
						Prices dated before then keep the rate in force on their own date
						{newRateBp !== null
							? `; prices dated on or after it will use ${formatRate(newRateBp)}.`
							: "."}
					</p>
				) : preview.productsRepriced === 0 && preview.productsInScope > 0 ? (
					<p>
						Every price of these products is dated before{" "}
						{formatRateDay(preview.startsAt)}, or already uses this rate. A
						price keeps the rate in force on its own date.
					</p>
				) : null}
				<p className="text-muted-foreground">
					{plural(preview.productsInScope, "product")} use this prefix.{" "}
					{plural(preview.productsGainingPrice, "product")} gain a price,{" "}
					{preview.productsLosingPrice} lose one.
				</p>
			</div>

			{preview.samples.length > 0 ? (
				<div className="overflow-x-auto">
					<table className="w-full text-left text-sm">
						<thead className="text-xs text-muted-foreground">
							<tr>
								<th className="py-1 pr-3 font-medium">Product</th>
								<th className="py-1 pr-3 font-medium">Variant</th>
								<th className="py-1 pr-3 font-medium">Rate</th>
								<th className="py-1 font-medium">Customer price</th>
							</tr>
						</thead>
						<tbody>
							{preview.samples.map((sample) => (
								<tr
									key={`${sample.productId}-${sample.variantSku ?? ""}-${sample.city}`}
									className="border-t align-top"
								>
									<td className="py-1.5 pr-3">{sample.productName}</td>
									<td className="py-1.5 pr-3">
										{sample.variantSku ?? "—"}
										{sample.city !== "__national" ? ` (${sample.city})` : ""}
									</td>
									<td className="py-1.5 pr-3 whitespace-nowrap">
										{sample.rateBpBefore === null
											? "none"
											: formatRate(sample.rateBpBefore)}{" "}
										→{" "}
										{sample.rateBpAfter === null
											? "none"
											: formatRate(sample.rateBpAfter)}
									</td>
									<td className="py-1.5">
										{sample.amountBefore !== null &&
										sample.amountBefore === sample.amountAfter ? (
											<>
												{formatPaise(sample.amountAfter)}{" "}
												<span className="text-muted-foreground">
													(unchanged, the price already includes GST; the rate
													is recorded)
												</span>
											</>
										) : (
											<>
												{sample.amountBefore === null
													? "not shown"
													: formatPaise(sample.amountBefore)}{" "}
												→{" "}
												{sample.amountAfter === null
													? "not shown"
													: formatPaise(sample.amountAfter)}
											</>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
					{preview.sampleTotal > preview.samples.length ? (
						<p className="mt-1 text-xs text-muted-foreground">
							Showing {preview.samples.length} of {preview.sampleTotal} prices
							that change.
						</p>
					) : null}
				</div>
			) : null}
		</div>
	);
}

/** The outcome of a save's re-price, and the way to finish one that ran out of time (R12). */
export function GstRepriceSummary({
	headline,
	result,
	onRepriceRemaining,
	repricing,
	error = null,
}: {
	headline: string;
	result: GstRepriceResult;
	onRepriceRemaining: () => void;
	repricing: boolean;
	/** Why the last "Re-price remaining" failed, if it did. */
	error?: string | null;
}) {
	const left = result.remaining + result.failed;
	return (
		<div className="grid gap-2 text-sm">
			<p className="font-semibold">
				{result.interrupted
					? `${headline} Re-pricing stopped on an error before it finished.`
					: `${headline} ${plural(result.repriced, "product")} re-priced.`}
			</p>
			{left > 0 || result.interrupted ? (
				<>
					<p>
						{result.interrupted
							? "Some customer prices may still use the old rate."
							: `${plural(left, "product")} still to re-price${result.failed > 0 ? ` (${result.failed} failed)` : ""}. Their customer prices use the old rate until they are.`}
					</p>
					<div>
						<Button
							variant="outline"
							size="sm"
							onClick={onRepriceRemaining}
							disabled={repricing}
						>
							{repricing ? "Re-pricing…" : "Re-price remaining"}
						</Button>
					</div>
					{error ? (
						<p role="alert" className="text-red-700">
							{error}
						</p>
					) : null}
				</>
			) : null}
		</div>
	);
}
